/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useMemo } from 'react';

import PropTypes from 'prop-types';

import { Locale, LocaleKeys } from '../../../core/locale';
import { CollectionActions } from '../../../store/collectionscreen/CollectionActions';
import { RightPreviewProps } from './RightPreviewProps';

import './RightPreview.scss';

const RightPreview: FunctionComponent<RightPreviewProps> = ({ highlightedOption, preview }) => {
    const previewElements = useMemo(
        () =>
            [
                preview.IncludeLabel && {
                    key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_INCLUDE),
                    value: preview.IncludeLabel,
                },
                preview.StartFromLabel && {
                    key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_START_FROM),
                    value: preview.StartFromLabel,
                },
                preview.RentOrBuyLabel && {
                    key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_RENT_OR_BUY),
                    value: preview.RentOrBuyLabel,
                },
                preview.RecordTypeLabel && {
                    key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_RECORD),
                    value: preview.RecordTypeLabel,
                },
                preview.ChannelRecordingLabel && {
                    key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_CHANNEL),
                    value: preview.ChannelRecordingLabel,
                },
                preview.VideoQualityLabel && {
                    key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_VIDEO_QUALITY),
                    value: preview.VideoQualityLabel,
                },
                preview.KeepAtMostLabel && {
                    key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_KEEP_AT_MOST),
                    value: preview.KeepAtMostLabel,
                },
                preview.KeepUntilLabel && {
                    key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_KEEP_UNTIL),
                    value: preview.KeepUntilLabel,
                },
                preview.StartRecordingEarlyLabel &&
                    preview.StopRecordingLateLabel && {
                        key: Locale.getString(LocaleKeys.RIGHT_PREVIEW_ELEMENT_START_STOP),
                        value: `${preview.StartRecordingEarlyLabel}/${preview.StopRecordingLateLabel}`,
                    },
            ].filter(Boolean) as { key: string; value: string }[],
        [preview],
    );

    const renderPreviewElements = () => (
        <div className="preview-elements-container">
            {previewElements.map((element, index) => (
                <div className="element" key={index}>
                    <span className="key">{element.key}: </span>
                    <span className="value">{element.value}</span>
                </div>
            ))}
        </div>
    );

    const renderOnePassOptionsPreview = () => (
        <>
            <div className="title">{`${preview.TitleLabel || ''}`}</div>
            {renderPreviewElements()}
        </>
    );

    const renderPreviewContent = () => {
        switch (highlightedOption) {
            case CollectionActions.CANCEL_ONEPASS_V2:
            case CollectionActions.MODIFY_ONEPASS_V2:
                return renderOnePassOptionsPreview();
            default:
                return null;
        }
    };

    return <div className="right-preview-container">{renderPreviewContent()}</div>;
};

RightPreview.propTypes = {
    highlightedOption: PropTypes.string.isRequired,
    preview: PropTypes.any.isRequired,
};

export default RightPreview;
